import React from 'react';
import { Check, Mail, Send, Smartphone, X } from 'lucide-react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import {
  buildInvitePermissionLists,
  pruneOverridesToPreset,
  resolveMemberCapabilities,
  type CapabilityMap,
  type PlatformAccess,
} from '../capabilitiesUi';
import type { RolePresetOption } from './RolePresetSelect';
import { UserAccessSettingsFields } from './UserAccessSettingsFields';
import { CapabilityDeviationEditor } from './CapabilityDeviationEditor';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { availableRoleEditorPermKeys } from '../roleEditorGroups';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';

type InviteChannel = 'email' | 'phone';

export type UserInviteModalProps = {
  canManage: boolean;
  presets: RolePresetOption[];
  loading: boolean;
  success: boolean;
  error: string | null;
  title?: string;
  subtitle?: string | null;
  initialChannel?: InviteChannel;
  initialEmail?: string;
  initialPhone?: string;
  onClose: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  /** @deprecated Prefer initialFirstName / initialLastName */
  initialFullName?: string;
  onSubmit: (payload: {
    channel: InviteChannel;
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
    full_name: string;
    role: 'member' | 'admin';
    platform_access: PlatformAccess;
    role_preset_id: string | null;
    capability_overrides: CapabilityMap;
  }) => Promise<void>;
};

const INVITE_ROLE_OPTIONS = [
  { value: 'member', label: 'Mitglied' },
  { value: 'admin', label: 'Admin' },
];

function splitFullName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function UserInviteModal({
  canManage,
  presets,
  loading,
  success,
  error,
  title = 'Nutzer einladen',
  subtitle = null,
  initialChannel = 'email',
  initialEmail = '',
  initialPhone = '',
  initialFirstName = '',
  initialLastName = '',
  initialFullName = '',
  onClose,
  onSubmit,
}: UserInviteModalProps) {
  const splitInitial = splitFullName(initialFullName);
  const [channel, setChannel] = React.useState<InviteChannel>(initialChannel);
  const [email, setEmail] = React.useState(initialEmail);
  const [phone, setPhone] = React.useState(initialPhone);
  const [firstName, setFirstName] = React.useState(initialFirstName || splitInitial.firstName);
  const [lastName, setLastName] = React.useState(initialLastName || splitInitial.lastName);
  const [role, setRole] = React.useState<'member' | 'admin'>('member');
  const [presetId, setPresetId] = React.useState('');
  const [access, setAccess] = React.useState<PlatformAccess>('office_and_field');
  const [overrides, setOverrides] = React.useState<CapabilityMap>({});
  // Zusammenfassung ohne Bereiche abgewählter Module (wie Rollen & Rechte).
  const { modules } = useModuleAccess();
  const availablePermKeys = React.useMemo(() => availableRoleEditorPermKeys(modules), [modules]);

  useEscapeClose(() => {
    if (!loading) onClose();
  });

  const officePresets = presets.filter((preset) => preset.default_platform_access !== 'field_only');
  const fieldPresets = presets.filter((preset) => preset.default_platform_access === 'field_only');
  const visiblePresets = channel === 'email' ? officePresets : fieldPresets.length ? fieldPresets : presets;

  React.useEffect(() => {
    if (!presetId && visiblePresets.length) {
      setPresetId(visiblePresets[0].id);
    }
  }, [presetId, visiblePresets]);

  const selectedPreset = presets.find((preset) => preset.id === presetId) ?? null;

  React.useEffect(() => {
    if (channel === 'phone') {
      setAccess('field_only');
      return;
    }
    if (selectedPreset) setAccess(selectedPreset.default_platform_access);
  }, [channel, selectedPreset]);

  const effectiveCaps = resolveMemberCapabilities({
    role,
    platformAccess: access,
    presetCapabilities: selectedPreset?.capabilities ?? {},
    overrideCapabilities: overrides,
  });
  const permissionLists = buildInvitePermissionLists(effectiveCaps, access, availablePermKeys);
  const presetCaps = selectedPreset?.capabilities ?? {};

  const nameReady = Boolean(firstName.trim()) && Boolean(lastName.trim());
  const submitEnabled =
    Boolean(presetId) &&
    nameReady &&
    (channel === 'phone' ? Boolean(phone.trim()) : Boolean(email.trim()));

  const handleChannelChange = (next: InviteChannel) => {
    setChannel(next);
    if (next === 'phone') setRole('member');
    const pool = next === 'email' ? officePresets : fieldPresets.length ? fieldPresets : presets;
    if (pool.length) setPresetId(pool[0].id);
    setOverrides({});
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !loading && onClose()}
      role="presentation"
    >
      <div
        className="flex max-h-[92vh] w-full max-w-[52.5rem] flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between gap-3 border-b border-app-border px-5 py-4">
          <div>
            <h2 className="text-base font-bold text-app-text">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-sm text-app-text-subtle">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {success ? (
          <div className="px-5 py-10 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <p className="font-semibold text-app-text">Einladung gesendet!</p>
            <p className="mt-1 text-sm text-app-text-subtle">
              {channel === 'phone'
                ? 'SMS mit Anleitung zum App-Download und Login ist unterwegs.'
                : 'Die E-Mail ist unterwegs.'}
            </p>
          </div>
        ) : (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              const first = firstName.trim();
              const last = lastName.trim();
              void onSubmit({
                channel,
                email: email.trim(),
                phone: phone.trim(),
                first_name: first,
                last_name: last,
                full_name: [first, last].filter(Boolean).join(' '),
                role,
                platform_access: access,
                role_preset_id: presetId || null,
                capability_overrides: pruneOverridesToPreset(presetCaps, overrides),
              });
            }}
          >
            <div className="flex-1 space-y-5 overflow-y-auto app-scrollbar px-5 py-4">
              {error ? (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
              ) : null}

              {canManage ? (
                <div className="inline-flex w-full rounded-lg border border-app-border bg-app-surface-muted p-0.5">
                  <button
                    type="button"
                    onClick={() => handleChannelChange('email')}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                      channel === 'email' ? 'bg-app-surface text-pipedrive-blue shadow-sm' : 'text-app-text-subtle'
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    Büro & App
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChannelChange('phone')}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                      channel === 'phone' ? 'bg-app-surface text-pipedrive-blue shadow-sm' : 'text-app-text-subtle'
                    }`}
                  >
                    <Smartphone className="h-4 w-4" />
                    Nur App
                  </button>
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                    Vorname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    placeholder="Max"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                    Nachname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                    placeholder="Mustermann"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
              </div>

              {channel === 'phone' ? (
                <div>
                  <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                    Telefonnummer für App-Login <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    autoComplete="tel"
                    placeholder="0170 1234567"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
              ) : (
                <div>
                  <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                    E-Mail-Adresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="kollegin@betrieb.de"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
              )}

              <UserAccessSettingsFields
                role={role}
                onRoleChange={(value) => setRole(value as 'member' | 'admin')}
                roleOptions={channel === 'phone' ? INVITE_ROLE_OPTIONS.filter((option) => option.value === 'member') : INVITE_ROLE_OPTIONS}
                showRoleSelect={canManage && channel !== 'phone'}
                roleDisabled={channel === 'phone'}
                presets={visiblePresets}
                presetId={presetId}
                onPresetChange={(nextId) => {
                  setPresetId(nextId);
                  const preset = presets.find((entry) => entry.id === nextId);
                  setOverrides((prev) => pruneOverridesToPreset(preset?.capabilities ?? {}, prev));
                }}
                permissionLists={permissionLists}
                extraContent={
                  <CapabilityDeviationEditor
                    presetCaps={presetCaps}
                    overrides={overrides}
                    access={access}
                    isAdmin={role === 'admin'}
                    onChange={setOverrides}
                  />
                }
              />
            </div>

            <div className="flex justify-end gap-2 border-t border-app-border px-5 py-4">
              <button type="button" onClick={onClose} disabled={loading} className={SETTINGS_BTN_OUTLINE}>
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={loading || !submitEnabled}
                className={`${SETTINGS_BTN_PRIMARY} gap-2`}
              >
                <Send className="h-4 w-4" />
                {loading ? 'Senden…' : 'Einladung senden'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
